import { Button, Input } from '@chopchop-se/ui';
import { ScanLine } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';

/**
 * The scanner-facing input.
 *
 * A Zebra handheld in DataWedge keystroke mode is a keyboard that types the
 * payload and presses Enter. So this is a plain form field with an explicit
 * submit — no global keydown listener, no inter-character timing heuristic.
 * The old WMS shipped two competing timing-based hooks (50 ms char gap, one
 * with a focus trap, one that bailed out whenever an input had focus) and lost
 * scans in both directions; docs/recon/printing-scanners.md §B9.
 *
 * Properties that matter on the floor:
 * - it keeps focus after a scan, so ten cartons are ten triggers and no taps;
 * - it clears itself on submit, so the next scan cannot append to the last one;
 * - it stays usable with a keyboard: the same field accepts a typed code.
 */
export interface ScanFieldProps {
  label: string;
  placeholder?: string;
  hint?: ReactNode;
  onScan: (code: string) => void;
  disabled?: boolean;
  busy?: boolean;
  /** Re-focus whenever this value changes (e.g. after a line was added). */
  refocusKey?: string | number;
  autoFocus?: boolean;
  submitLabel?: string;
  testId?: string;
}

export function ScanField({
  label,
  placeholder,
  hint,
  onScan,
  disabled = false,
  busy = false,
  refocusKey,
  autoFocus = true,
  submitLabel,
  testId = 'scan-field',
}: ScanFieldProps): ReactNode {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!disabled && autoFocus) inputRef.current?.focus();
  }, [disabled, autoFocus, refocusKey]);

  function submit(event: FormEvent): void {
    event.preventDefault();
    const code = value.trim();
    if (code.length === 0 || disabled || busy) return;
    setValue('');
    onScan(code);
    // Focus survives the parent's re-render: the operator's next trigger-pull
    // must land in this field, not nowhere.
    inputRef.current?.focus();
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-1.5" data-testid={testId}>
      <label className="text-sm font-medium text-ink" htmlFor={`${testId}-input`}>
        {label}
      </label>
      <div className="flex gap-2">
        <Input
          id={`${testId}-input`}
          ref={inputRef}
          className="flex-1"
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
          }}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          leading={<ScanLine className="size-4 text-ink-muted" aria-hidden />}
          aria-label={label}
        />
        <Button type="submit" loading={busy} disabled={disabled || value.trim().length === 0}>
          {submitLabel ?? label}
        </Button>
      </div>
      {hint ? <p className="text-xs text-ink-muted">{hint}</p> : null}
    </form>
  );
}
